import {
  APIEntityType,
  type ArdoqId,
  FilterInterfaceFilter,
} from '@ardoq/api-types';
import { ReferenceLabelSource } from '@ardoq/data-model';
import { OtherLabelsData } from './types';
import { getRelevantLabelFormatting } from './otherLabelUtils';
import { labelFormattingLabels } from './labelFormattingLabels';
import { LabelsInterface } from '../labelsInterface/labelsInterface';
import { newConditionalFormattingMultiLabelStyle } from './multiLabelStyles';
import { type I18nLocale } from '@ardoq/i18n';

const REFERENCE_LABEL_SOURCES_CONSIDERED_AS_SHOW_REFERENCE_TYPE =
  new Set<string>([
    ReferenceLabelSource.REFERENCE_TYPE,
    ReferenceLabelSource.DISPLAY_TEXT_OR_REFERENCE_TYPE,
  ]);

export const getIsShowReferenceTypeApplied = (
  allRefLabelFilterAttributes: FilterInterfaceFilter[]
) => {
  // currently, show reference type is a global value, so we don't care about specific ref types
  if (
    allRefLabelFilterAttributes.some(
      ({ value }) => value === ReferenceLabelSource.NONE
    )
  ) {
    return false;
  }

  return (
    !allRefLabelFilterAttributes.length ||
    allRefLabelFilterAttributes.some(({ value }) =>
      REFERENCE_LABEL_SOURCES_CONSIDERED_AS_SHOW_REFERENCE_TYPE.has(value)
    )
  );
};

export const getOtherLabelsDataForReference = (
  labelsInterface: LabelsInterface,
  referenceId: ArdoqId
): OtherLabelsData | null => {
  const labelFormatting = labelsInterface.getLabelFormattingRules();
  const workspaceId = labelsInterface.getReferenceRootWorkspaceId(referenceId);
  const referenceTypeName =
    labelsInterface.getGlobalReferenceType(referenceId)?.name;
  if (
    !workspaceId ||
    !referenceTypeName ||
    !labelsInterface.getWorkspaceModelId(workspaceId)
  ) {
    return null;
  }
  return {
    entityId: referenceId,
    fields: labelsInterface.getReferenceFields(referenceId),
    labelFormatting,
    entityTypeName: referenceTypeName,
  };
};

export const getOtherLabelsForReference = (
  labelsInterface: LabelsInterface,
  referenceId: ArdoqId,
  i18nLocale: I18nLocale
) => {
  const labelsData = getOtherLabelsDataForReference(
    labelsInterface,
    referenceId
  );
  if (!labelsData) {
    return;
  }

  const relevantFormatting =
    getRelevantLabelFormatting({
      apiEntityType: APIEntityType.REFERENCE,
      ...labelsData,
    }) ?? undefined;

  // TODO: style should be evaluated based on conditional formatting when we pick up the multi-label coloring
  const multiLabelStyle = newConditionalFormattingMultiLabelStyle;

  return (
    relevantFormatting &&
    relevantFormatting.flatMap(
      labelFormattingLabels({
        labelsInterface,
        modelId: referenceId,
        entityFields: labelsData.fields,
        entityType: APIEntityType.REFERENCE,
        multiLabelStyle,
        i18nLocale,
      })
    )
  );
};
